import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "./ui/sheet";
import { Button } from "./ui/button";
import {
  Star, BookOpen, Music, Headphones, PawPrint, Salad, HandHeart,
  Leaf, Mic, Moon, Bell, Target, Crown, Sparkles, User,
} from "lucide-react";

/**
 * ExploreDrawer — a pull-up bottom sheet housing every "do something" feature
 * that used to live as a dense 12-tile grid on the dashboard. Keeps the main
 * dashboard sacred and altar-like while leaving every action one tap away.
 *
 * Grouped into "Inner Work / Body / Sound & Mind / Tools" so browsing feels
 * intentional instead of overwhelming.
 */
const features = [
  // Inner Work
  { name: "Angel Numbers", href: "/angel-numbers", icon: Star,       group: "Inner Work", gradient: "from-amber-400 to-orange-500" },
  { name: "Spirit Animals", href: "/spirit-animals", icon: PawPrint, group: "Inner Work", gradient: "from-cyan-400 to-blue-500" },
  { name: "Dream Journal", href: "/dreams", icon: Moon,              group: "Inner Work", gradient: "from-indigo-400 to-purple-500" },
  { name: "Journal", href: "/journal", icon: BookOpen, premium: true, group: "Inner Work", gradient: "from-rose-400 to-pink-500" },

  // Sound & Mind
  { name: "Meditations", href: "/guided-meditations", icon: Headphones, premium: true, group: "Sound & Mind", gradient: "from-violet-500 to-purple-600" },
  { name: "Sound Sanctuary", href: "/music", icon: Music, premium: true, group: "Sound & Mind", gradient: "from-emerald-500 to-teal-600" },
  { name: "My Affirmations", href: "/custom-affirmations", icon: Mic, premium: true, group: "Sound & Mind", gradient: "from-purple-400 to-indigo-500" },

  // Body
  { name: "Nutrition", href: "/nutrition", icon: Salad,              group: "Body", gradient: "from-green-400 to-emerald-500" },
  { name: "Hobbies", href: "/mindful-hobbies", icon: Leaf,           group: "Body", gradient: "from-lime-400 to-green-500" },
  { name: "Baby Go to Therapy", href: "/therapy", icon: HandHeart,   group: "Body", gradient: "from-pink-400 to-rose-500" },

  // Tools
  { name: "Reminders", href: "/reminder-settings", icon: Bell,       group: "Tools", gradient: "from-blue-400 to-indigo-500" },
  { name: "Focus Timer", href: "/pomodoro", icon: Target,            group: "Tools", gradient: "from-red-400 to-orange-500" },
  { name: "Settings", href: "/profile", icon: User,                  group: "Tools", gradient: "from-slate-400 to-slate-600" },
];

const ExploreDrawer = ({ isPremium, ritualsCompleted = 0, ritualsTotal = 0, streakDays = 0 }) => {
  const [open, setOpen] = useState(false);

  // Close with Escape for keyboard users
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const grouped = features.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  const showBadge = ritualsTotal > 0;
  const allDone = showBadge && ritualsCompleted >= ritualsTotal;

  // "Streak Fire" — when the user hits a 7-day streak, the leaf glow shifts
  // from emerald to warm amber for a quiet, always-on celebration. No popup,
  // no modal — just the FAB quietly recognizing their dedication.
  const onFire = streakDays >= 7;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {/* Floating trigger button — bottom-right, subtle pulse on idle */}
        {/* Glowing Emerald LEAF FAB — echoes the Lala Land logo leaf. Solid
            emerald (matches brand), leaf icon with heavy green glow via
            drop-shadow filter, asymmetric drift. When the user hits a 7-day
            streak the whole aura warms to amber ("on fire") as a quiet reward. */}
        <Button
          size="icon"
          className={`fixed bottom-48 right-3 z-40 h-14 w-14 rounded-full text-white border-0 hover:scale-110 transition-transform ${
            onFire ? "bg-amber-500 hover:bg-amber-400" : "bg-emerald-600 hover:bg-emerald-500"
          }`}
          style={{
            animation: onFire
              ? 'float-drift-2 8.5s ease-in-out infinite, soft-glow-amber 3.5s ease-in-out infinite'
              : 'float-drift-2 8.5s ease-in-out infinite, soft-glow-emerald 3.8s ease-in-out infinite',
            boxShadow: onFire
              ? '0 0 0 3px rgba(251,191,36,0.15), 0 10px 30px rgba(245,158,11,0.45)'
              : '0 0 0 3px rgba(16,185,129,0.12), 0 10px 30px rgba(16,185,129,0.35)',
          }}
          data-testid="explore-drawer-trigger"
          data-streak-fire={onFire ? "true" : "false"}
          aria-label={
            onFire
              ? `Explore — ${streakDays}-day streak on fire`
              : showBadge
                ? `Explore — ${ritualsCompleted} of ${ritualsTotal} rituals today`
                : "Explore all features"
          }
        >
          <Leaf
            className={onFire ? "h-6 w-6 text-amber-100" : "h-6 w-6 text-emerald-200"}
            style={{
              filter: onFire
                ? 'drop-shadow(0 0 4px rgba(253,230,138,0.95)) drop-shadow(0 0 10px rgba(251,191,36,0.7)) drop-shadow(0 0 20px rgba(245,158,11,0.5))'
                : 'drop-shadow(0 0 4px rgba(110,231,183,0.9)) drop-shadow(0 0 10px rgba(52,211,153,0.6)) drop-shadow(0 0 20px rgba(52,211,153,0.35))',
            }}
          />
          {/* Tiny streak badge when on fire */}
          {onFire && (
            <span
              className="absolute -bottom-1.5 -left-1 bg-amber-400 text-amber-950 text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-background shadow-md"
              data-testid="explore-streak-fire-badge"
              aria-hidden="true"
              title={`${streakDays}-day streak`}
            >
              🔥{streakDays}
            </span>
          )}
          {showBadge && (
            <span
              className={`absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center border-2 border-background shadow-md ${
                allDone
                  ? "bg-amber-400 text-amber-950 ring-2 ring-amber-300/50 animate-pulse"
                  : "bg-white text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200"
              }`}
              data-testid="explore-rituals-badge"
              aria-hidden="true"
            >
              {allDone ? "✨" : `${ritualsCompleted}/${ritualsTotal}`}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl max-h-[85vh] overflow-y-auto"
        data-testid="explore-drawer"
      >
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-4 w-4 text-primary" />
            Explore Lala Land
            {showBadge && (
              <span
                className={`ml-auto text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                  allDone
                    ? "bg-amber-400/20 text-amber-700 dark:text-amber-300"
                    : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                }`}
                data-testid="explore-rituals-drawer-badge"
              >
                {allDone ? "✨ All rituals done today" : `${ritualsCompleted} of ${ritualsTotal} rituals today`}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>
            Every ritual, one tap away. Your dashboard stays sacred — this is your menu.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-6 pb-8">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.14em] mb-3 px-1">
                {group}
              </p>
              <div className="grid grid-cols-4 gap-3">
                {items.map((f) => (
                  <Link
                    key={f.name}
                    to={f.href}
                    onClick={() => setOpen(false)}
                    className="flex flex-col items-center p-2 rounded-2xl hover:bg-primary/5 transition-colors group relative"
                    data-testid={`explore-${f.name.toLowerCase().replace(/\s+/g, "-")}-link`}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center shadow-md group-hover:scale-105 transition-transform`}>
                      <f.icon className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-[11px] font-medium text-primary text-center mt-2 leading-tight">
                      {f.name}
                    </span>
                    {f.premium && !isPremium && (
                      <Crown className="h-3 w-3 text-accent absolute top-1 right-1" />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ExploreDrawer;
